import { shallowReactive, ref, type Ref, watch, computed } from 'vue';
import { type Channel, type ProgramGuideItem, PlayItem } from "@ca.vmedia.tv/domains";
import type { IQuickViewPanel, ExposeInterface } from './types';
import { useGuideStore } from "@/stores/Guide";
import { Strings } from "@/locales/types/enums/strings";
import { useI18n } from "vue-i18n";
import { storeToRefs } from 'pinia';
import { useProgramInfo } from '../useProgramInfo/useProgramInfo';

export const useGuideQuickViewPanel = (): ExposeInterface => {
    const GuideStoreModule = useGuideStore();
    const {
        channelsIdsGuide: channels,
        focusedChannel,
    } = storeToRefs(GuideStoreModule)


    const quickViewPanel: IQuickViewPanel = shallowReactive({
        srcImg: "",
        programName: "",
        channelNumber: "",
        programDescription: "",
        channelName: "",
        advisoryText: "",
        highDefinition: false,
        closedCaptions: false,
        descriptiveAudio: false,
        isRestartAvailable: false
    });

    const isProgramAvailable: Ref<boolean> = ref(true);

    const lengthDescription: number = 250;
    const widthImg: number = 360;
    const { t } = useI18n();
    const isChannelsAvailable = computed((): boolean => channels.value.length > 0);

    const setQuickViewPanel = (focusedProgram: ProgramGuideItem | null = null, channel: Channel | null): void => {
        _clearQuickViewPanel();

        if (focusedProgram !== null) {
            _assignProgram(focusedProgram, channel);
        } else if (focusedChannel.value) {
            _assignChannel();
        }

    };

    const _updateQuickViewPanelChannel = (channel: Channel): void => {
        quickViewPanel.channelName = channel.name || "";
        quickViewPanel.channelNumber = channel.accessNum || "";
    };

    const _assignProgram = (focusedProgram: ProgramGuideItem, channel: Channel | null): void => {
        const {
            programName,
            advisoryText,
            description,
            closedCaptions,
            descriptiveAudio
        } = useProgramInfo(focusedProgram, focusedChannel.value, null);
        const {
            image,
        } = focusedProgram;

        quickViewPanel.isRestartAvailable = PlayItem.isRestartAvailable(focusedProgram, channel);
        quickViewPanel.srcImg = image?.startsWith("http") ? `${image}&width=${widthImg}` : '';
        quickViewPanel.programName = programName || "";
        quickViewPanel.programDescription = description;
        quickViewPanel.descriptiveAudio = descriptiveAudio ?? false;
        quickViewPanel.closedCaptions = closedCaptions ?? false;
        quickViewPanel.advisoryText = advisoryText || "";

        if (quickViewPanel.programDescription.length > lengthDescription) {
            quickViewPanel.programDescription = `${quickViewPanel.programDescription.slice(0, lengthDescription).trim() + "..."}`;
        }

        if (focusedChannel.value) {
            _updateQuickViewPanelChannel(focusedChannel.value);
        }

        isProgramAvailable.value = true;
    };

    const _assignChannel = (): void => {
        if (!focusedChannel.value) {
            return;
        }
        const { image, name, accessNum, advisory } = focusedChannel.value;

        quickViewPanel.srcImg = image?.startsWith("http") ? image : "";
        quickViewPanel.channelName = name || "";
        quickViewPanel.channelNumber = accessNum || "";
        isProgramAvailable.value = false;
        quickViewPanel.advisoryText = typeof advisory === 'number' ? t(`${Strings.ADVISORY_TEXT}.${advisory}`).toString() : ""; 
    };

    const _clearQuickViewPanel = () => {
        quickViewPanel.srcImg = "";
        quickViewPanel.programName = "";
        quickViewPanel.channelNumber = "";
        quickViewPanel.programDescription = "";
        quickViewPanel.channelName = "";
        quickViewPanel.advisoryText = "";
        quickViewPanel.highDefinition = false;
        quickViewPanel.closedCaptions = false;
        quickViewPanel.descriptiveAudio = false;
        quickViewPanel.isRestartAvailable = false;
    }

    watch(
        channels,
        (value: number[]) => {
            if (!value.length) {
                _clearQuickViewPanel();
            }
        }
    );

    return {
        quickViewPanel,
        setQuickViewPanel,
        isProgramAvailable,
        isChannelsAvailable,
    };
};